import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  ServerIcon,
  ChartBarIcon,
  CogIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  cachePreferencesService,
  CachePreference,
  PlatformCacheStatistic,
  GlobalCacheStatistics
} from '../../services/cachePreferencesService';
import PlatformLogo from './PlatformLogo';
import { usePreloadStore } from '../../store/preloadStore';
import { getPlatformDisplayName, shouldHidePlatform } from '../../utils/platformUtils';

interface GlobalCacheSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalCacheSettingsModal: React.FC<GlobalCacheSettingsModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'global' | 'statistics'>('global');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Preload store for platform data
  const { getPlatforms } = usePreloadStore();

  // Global settings state
  const [globalSettings, setGlobalSettings] = useState<CachePreference | null>(null);
  const [globalCacheEnabled, setGlobalCacheEnabled] = useState(true);
  const [globalCacheDuration, setGlobalCacheDuration] = useState(7);

  // Statistics state
  const [platformStatistics, setPlatformStatistics] = useState<PlatformCacheStatistic[]>([]);
  const [globalStatistics, setGlobalStatistics] = useState<GlobalCacheStatistics | null>(null);

  // Enhanced platform statistics with logo data and filtering
  const getEnhancedPlatformStatistics = () => {
    const platforms = getPlatforms();
    
    return platformStatistics
      .filter(stat => !shouldHidePlatform(stat.platform_name))
      .map(stat => {
        // Find matching platform by name or type to get logo data
        const matchingPlatform = platforms.find(p => 
          p.name === stat.platform_name || 
          p.platform_type === stat.platform_type ||
          p.name.toLowerCase() === stat.platform_name.toLowerCase()
        );
        
        return {
          ...stat,
          logo_base64: matchingPlatform?.logo_base64,
          displayName: getPlatformDisplayName(stat.platform_name)
        };
      });
  };

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAllData();
    }
  }, [isOpen]);

  const loadAllData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadGlobalSettings(),
        loadCacheStatistics()
      ]);
    } catch (err: any) {
      setError(err.message || 'Failed to load cache settings');
    } finally {
      setIsLoading(false);
    }
  };

  const loadGlobalSettings = async () => {
    try {
      const response = await cachePreferencesService.getGlobalSettings();
      setGlobalSettings(response.preference);
      setGlobalCacheEnabled(response.preference.cache_enabled);
      setGlobalCacheDuration(response.preference.cache_duration_days);
    } catch (err: any) {
      console.error('Failed to load global settings:', err);
    }
  };


  const loadCacheStatistics = async () => {
    try {
      const response = await cachePreferencesService.getCacheStatistics();
      setPlatformStatistics(response.platform_statistics);
      setGlobalStatistics(response.global_statistics);
    } catch (err: any) {
      console.error('Failed to load cache statistics:', err);
    }
  };

  const handleUpdateGlobalSettings = async () => {
    if (!globalSettings) return;
    
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const response = await cachePreferencesService.updateGlobalSettings({
        cache_enabled: globalCacheEnabled,
        cache_duration_days: globalCacheDuration
      });
      
      setGlobalSettings(response.preference);
      setSuccessMessage(response.message);
    } catch (err: any) {
      setError(err.message || 'Failed to update global settings');
    } finally {
      setIsLoading(false);
    }
  };


  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="relative bg-theme-bg dark:bg-dark-theme-bg rounded-lg shadow-2xl w-[95vw] h-[85vh] max-w-6xl overflow-hidden border border-theme-border dark:border-dark-theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full md:flex-row">
          {/* Sidebar */}
          <div className="flex flex-col flex-shrink-0 p-4 w-full border-b md:w-64 bg-theme-bg dark:bg-dark-theme-bg md:rounded-l-lg md:p-6 md:border-b-0 md:border-r border-theme-border dark:border-dark-theme-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-theme-text dark:text-dark-theme-text">
                Cache Settings
              </h2>
              <button
                onClick={onClose}
                className="text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text"
                aria-label="Close cache settings"
                title="Close cache settings modal"
              >
                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>

            {/* Tab Navigation */}
            <nav className="space-y-2">
              {[
                { id: 'global', label: 'Global Settings', icon: CogIcon },
                { id: 'statistics', label: 'Statistics', icon: ChartBarIcon }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-left transition-colors ${
                    activeTab === id
                      ? 'bg-theme-accent/10 dark:bg-theme-accent/10 text-theme-accent dark:text-theme-accent'
                      : 'text-theme-text-secondary dark:text-dark-theme-text-secondary hover:text-theme-text dark:hover:text-dark-theme-text hover:bg-theme-bg-secondary dark:hover:bg-dark-theme-bg-secondary'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex flex-col flex-1 min-h-0">
            {/* Content Header */}
            <div className="p-4 border-b md:p-6 border-theme-border dark:border-dark-theme-border">
              <div className="flex items-center space-x-3">
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-theme-accent dark:bg-theme-accent">
                  {activeTab === 'global' && <CogIcon className="w-4 h-4 text-white" />}
                  {activeTab === 'statistics' && <ChartBarIcon className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-theme-text dark:text-dark-theme-text">
                    {activeTab === 'global' && 'Global Cache Settings'}
                    {activeTab === 'statistics' && 'Cache Statistics'}
                  </h3>
                  <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
                    {activeTab === 'global' && 'Set default cache preferences for all platforms'}
                    {activeTab === 'statistics' && 'View cache performance metrics and analytics'}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="px-4 md:px-6">
              {error && (
                <div className="p-3 mb-4 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    <button 
                      onClick={clearMessages} 
                      className="ml-auto text-red-600 dark:text-red-400"
                      aria-label="Dismiss error message"
                      title="Close error message"
                    >
                      <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="p-3 mb-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <p className="text-sm text-green-700 dark:text-green-300">{successMessage}</p>
                    <button 
                      onClick={clearMessages} 
                      className="ml-auto text-green-600 dark:text-green-400"
                      aria-label="Dismiss success message"
                      title="Close success message"
                    >
                      <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Tab Content */}
            <div className="overflow-y-auto flex-1 p-4 md:p-6">
              {isLoading && (
                <div className="flex justify-center items-center h-64">
                  <div className="flex items-center space-x-2">
                    <ArrowPathIcon className="w-5 h-5 animate-spin text-theme-accent dark:text-theme-accent" />
                    <span className="text-theme-text-secondary dark:text-dark-theme-text-secondary">Loading...</span>
                  </div>
                </div>
              )}

              {!isLoading && activeTab === 'global' && (
                <GlobalSettingsTab
                  globalCacheEnabled={globalCacheEnabled}
                  globalCacheDuration={globalCacheDuration}
                  onCacheEnabledChange={setGlobalCacheEnabled}
                  onCacheDurationChange={setGlobalCacheDuration}
                  onSave={handleUpdateGlobalSettings}
                  isLoading={isLoading}
                />
              )}


              {!isLoading && activeTab === 'statistics' && (
                <StatisticsTab
                  platformStatistics={getEnhancedPlatformStatistics()}
                  globalStatistics={globalStatistics}
                  onRefresh={loadCacheStatistics}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Global Settings Tab Component
const GlobalSettingsTab: React.FC<{
  globalCacheEnabled: boolean;
  globalCacheDuration: number;
  onCacheEnabledChange: (enabled: boolean) => void;
  onCacheDurationChange: (duration: number) => void;
  onSave: () => void;
  isLoading: boolean;
}> = ({ globalCacheEnabled, globalCacheDuration, onCacheEnabledChange, onCacheDurationChange, onSave, isLoading }) => (
  <div className="space-y-6">
    <div className="p-4 rounded-lg border bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border">
      <h4 className="mb-4 text-sm font-medium text-theme-text-primary dark:text-dark-theme-text-primary">
        Default Cache Settings
      </h4>
      <p className="mb-4 text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
        These settings will be used as defaults for all platforms unless overridden by platform-specific settings.
      </p>
      
      <div className="space-y-4">
        {/* Cache Enabled Toggle */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <ServerIcon className="w-4 h-4 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
            <label className="text-sm font-medium text-theme-text-primary dark:text-dark-theme-text-primary">
              Enable Caching
            </label>
          </div>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => onCacheEnabledChange(!globalCacheEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-theme-accent focus:ring-offset-2 ${
              globalCacheEnabled 
                ? 'bg-theme-accent dark:bg-dark-theme-accent' 
                : 'bg-theme-border dark:bg-dark-theme-border'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                globalCacheEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Cache Duration */}
        {globalCacheEnabled && (
          <div>
            <div className="flex items-center mb-2 space-x-2">
              <ClockIcon className="w-4 h-4 text-theme-text-secondary dark:text-dark-theme-text-secondary" />
              <label htmlFor="global-cache-duration" className="text-sm font-medium text-theme-text-primary dark:text-dark-theme-text-primary">
                Default Cache Duration
              </label>
            </div>
            <select
              id="global-cache-duration"
              value={globalCacheDuration}
              onChange={(e) => onCacheDurationChange(parseInt(e.target.value, 10))}
              disabled={isLoading}
              className="px-3 py-2 w-full text-sm rounded-md border border-theme-border dark:border-dark-theme-border bg-theme-bg dark:bg-dark-theme-bg text-theme-text dark:text-dark-theme-text focus:ring-2 focus:ring-theme-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              title="Set default cache duration for all platforms"
            >
              <option value={1}>1 day</option>
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
        )}
      </div>
    </div>

    <div className="flex justify-end">
      <button
        onClick={onSave}
        disabled={isLoading}
        className="px-4 py-2 text-white rounded-lg transition-opacity bg-theme-accent dark:bg-theme-accent hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? 'Saving...' : 'Save Global Settings'}
      </button>
    </div>
  </div>
);


// Enhanced platform statistics type
type EnhancedPlatformCacheStatistic = PlatformCacheStatistic & {
  logo_base64?: string;
  displayName: string;
};

// Statistics Tab Component
const StatisticsTab: React.FC<{
  platformStatistics: EnhancedPlatformCacheStatistic[];
  globalStatistics: GlobalCacheStatistics | null;
  onRefresh: () => void;
}> = ({ platformStatistics, globalStatistics, onRefresh }) => (
  <div className="space-y-6">
    {/* Global Statistics */}
    {globalStatistics && (
      <div className="p-4 rounded-lg border bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-theme-text dark:text-dark-theme-text">
            Global Cache Statistics
          </h4>
          <button
            onClick={onRefresh}
            className="px-2 py-1 text-xs text-white rounded transition-opacity bg-theme-accent hover:opacity-90"
          >
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-lg font-semibold text-theme-text dark:text-dark-theme-text">
              {globalStatistics.total_cached_items}
            </p>
            <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Total Items</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
              {globalStatistics.total_fresh_items}
            </p>
            <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Fresh Items</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-theme-accent dark:text-theme-accent">
              {globalStatistics.overall_hit_potential}
            </p>
            <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Hit Rate</p>
          </div>
        </div>
      </div>
    )}

    {/* Platform Statistics */}
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-theme-text dark:text-dark-theme-text">
        Platform Statistics
      </h4>
      {platformStatistics.length === 0 ? (
        <div className="py-8 text-center">
          <ChartBarIcon className="mx-auto mb-4 w-12 h-12 text-theme-text-tertiary dark:text-dark-theme-text-tertiary" />
          <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary">No cache statistics available</p>
        </div>
      ) : (
        <div className="space-y-3">
          {platformStatistics.map((stat) => (
            <div
              key={stat.platform_id}
              className="p-4 rounded-lg border border-theme-border dark:border-dark-theme-border"
            >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center space-x-3">
                  <PlatformLogo 
                    platformName={stat.platform_type} 
                    size="sm" 
                    logoBase64={stat.logo_base64}
                  />
                  <div>
                    <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text">
                      {stat.displayName}
                    </h5>
                    <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
                      {stat.cache_duration_days} days cache duration
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-theme-accent dark:text-theme-accent">
                    {stat.cache_hit_potential}
                  </p>
                  <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Hit Rate</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-theme-text dark:text-dark-theme-text">
                    {stat.total_cached_items}
                  </p>
                  <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-green-600 dark:text-green-400">{stat.fresh_items}</p>
                  <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Fresh</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-red-600 dark:text-red-400">{stat.expired_items}</p>
                  <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">Expired</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default GlobalCacheSettingsModal;